import { useEffect, useRef } from "react";
import {
  Scene,
  WebGLRenderer,
  PerspectiveCamera,
  BoxGeometry,
  MeshBasicMaterial,
  InstancedMesh,
  InstancedBufferAttribute,
  CanvasTexture,
  NormalBlending,
} from "three";

interface HeroBackgroundProps {
  className?: string;
}

export default function HeroBackground({ className = "" }: HeroBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const rowCount = 20;
    const columnCount = 64;
    const layerCount = 2;

    const getSize = () => ({
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const { width: initWidth, height: initHeight } = getSize();

    const camera = new PerspectiveCamera(60, initWidth / initHeight, 1, 1000);
    camera.position.set(0, 6, 6);
    camera.lookAt(0, 0, 1);

    const scene = new Scene();

    const geom = new BoxGeometry();
    const rowCol: number[] = [];

    for (let i = 0; i < rowCount; i++) {
      for (let j = 0; j < layerCount; j++) {
        for (let k = 0; k < columnCount; k++) {
          rowCol.push(i);
          rowCol.push(k);
          rowCol.push(j);
        }
      }
    }

    geom.setAttribute(
      "rcl",
      new InstancedBufferAttribute(new Float32Array(rowCol), 3)
    );

    // Build a heavy brushed-metal frame texture.
    // Dark base + dense brushed lines + strong polished rim.
    const canvas = document.createElement("canvas");
    const size = 128;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, size, size);

      // Dark cool base (deep tech blue-violet metal).
      const baseGrad = ctx.createLinearGradient(0, 0, 0, size);
      baseGrad.addColorStop(0, "rgba(60,58,130,0.95)");
      baseGrad.addColorStop(0.5, "rgba(38,36,95,0.95)");
      baseGrad.addColorStop(1, "rgba(22,20,65,0.95)");
      ctx.fillStyle = baseGrad;
      ctx.fillRect(0, 0, size, size);

      // Dense brushed-metal scanlines.
      for (let y = 0; y < size; y += 1) {
        const a = 0.05 + Math.random() * 0.14;
        ctx.fillStyle = `rgba(220,220,255,${a})`;
        ctx.fillRect(0, y, size, 1);
      }
      for (let y = 0; y < size; y += 2) {
        const a = 0.06 + Math.random() * 0.10;
        ctx.fillStyle = `rgba(10,8,30,${a})`;
        ctx.fillRect(0, y, size, 1);
      }

      // Vertical micro brush (extra metal grain).
      for (let x = 0; x < size; x += 4) {
        const a = 0.03 + Math.random() * 0.05;
        ctx.fillStyle = `rgba(255,255,255,${a})`;
        ctx.fillRect(x, 0, 1, size);
      }

      // Strong top sheen (polished light reflection).
      const sheen = ctx.createLinearGradient(0, 0, 0, size * 0.5);
      sheen.addColorStop(0, "rgba(255,255,255,0.32)");
      sheen.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, size, size * 0.5);

      // Bottom darker shadow band (under-bevel).
      const shadow = ctx.createLinearGradient(0, size * 0.6, 0, size);
      shadow.addColorStop(0, "rgba(0,0,0,0)");
      shadow.addColorStop(1, "rgba(0,0,0,0.35)");
      ctx.fillStyle = shadow;
      ctx.fillRect(0, size * 0.6, size, size * 0.4);

      // Polished bright metal rim.
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(235,230,255,1.0)";
      ctx.strokeRect(2, 2, size - 4, size - 4);

      // Dark inner groove for thickness.
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(8,6,25,0.9)";
      ctx.strokeRect(5, 5, size - 10, size - 10);
    }

    const map = new CanvasTexture(canvas);
    map.anisotropy = 4;
    const material = new MeshBasicMaterial({
      map,
      transparent: true,
      blending: NormalBlending,
      depthWrite: false,
    });

    const time = { value: 0 };
    const lightTimeUniform = { value: 0 };

    material.onBeforeCompile = (shader) => {
      shader.uniforms.time = time;
      shader.uniforms.lightTime = lightTimeUniform;

      shader.vertexShader = shader.vertexShader
        .split("#include <common>")
        .join(
          `
uniform float time;
uniform float lightTime;
attribute vec3 rcl;
varying vec3 vTintColor;
varying float vFaceShade;
varying float vSpecular;
varying float vDepthFog;
#include <common>
          `
        )
        .split("#include <project_vertex>")
        .join(
          `
const float columnCount = float(${columnCount});
const float arc = 2.0 * 3.14159265359 / columnCount;
const float oneStep = 0.283;

float shift = 3.0 - fract(time) * oneStep;

float radius = shift;
float zShift = 0.0;
int x = int(rcl.x);
for (int i = 0; i < x; i++) {
  radius += radius * arc;
  zShift += radius * arc;
}

vec4 mvPosition = vec4( transformed, 1.0 );

if (mvPosition.z > 0.0) {
  radius += radius * arc;
}

mvPosition.xz *= radius * arc;
mvPosition.z += zShift + shift;

float t = sin(rcl.y/5.3)*1.1
        + sin(rcl.y/1.3)*1.5
        + cos(rcl.y/1.7)*2.5;

t = 2.0 - rcl.x + abs(t) + fract(time);
t += rcl.z*abs(sin(rcl.y));
t = max(t, 0.);
mvPosition.y -= t*t*t + rcl.z;

float angle = rcl.y * arc;
float sn = sin(angle);
float cs = cos(angle);
mvPosition.xz = mvPosition.xz * mat2(cs, -sn, sn, cs);

// Rotate the normal the same way so we can shade faces (fake lighting).
vec3 nRot = normal;
nRot.xz = nRot.xz * mat2(cs, -sn, sn, cs);

mvPosition = modelViewMatrix * mvPosition;
gl_Position = projectionMatrix * mvPosition;

// ---- Per-face shading: low diffuse (metals are reflection-dominant) ----
float topness    = clamp(nRot.y, 0.0, 1.0);
float bottomness = clamp(-nRot.y, 0.0, 1.0);
float sideness   = 1.0 - topness - bottomness;

float shade = 0.32
            + topness    * 0.35
            + sideness   * 0.14
            - bottomness * 0.20;

// ---- ROTATING light sources -> highlights flow across the surface ----
// Uses independent lightTime so highlights orbit even when not scrolling.
float lc = cos(lightTime);
float ls = sin(lightTime);

// Key light rotates around the cube horizon.
vec3 keyLight  = normalize(vec3(lc * 0.8, 0.75, ls * 0.8));
// Fill light orbits opposite side, also drifting vertically.
vec3 fillLight = normalize(vec3(-lc * 0.7, 0.3 + sin(lightTime * 0.7) * 0.25, -ls * 0.7));
// Third cool rim light, slower orbit.
vec3 rimLight  = normalize(vec3(sin(lightTime * 0.5) * 0.9, 0.4, cos(lightTime * 0.5) * 0.9));

float NdotKey  = max(dot(nRot, keyLight), 0.0);
float NdotFill = max(dot(nRot, fillLight), 0.0);
float NdotRim  = max(dot(nRot, rimLight), 0.0);
shade += NdotKey * 0.30 + NdotFill * 0.16 + NdotRim * 0.10;

vFaceShade = clamp(shade, 0.22, 1.35);

// ---- Heavy metallic specular: ultra-sharp + medium + broad layers ----
vec3 viewDir = normalize(vec3(0.0, 0.4, 1.0));

// Ultra-sharp key highlight: tight, blazing (chrome-like).
vec3 hKey = normalize(keyLight + viewDir);
float sKey = pow(max(dot(nRot, hKey), 0.0), 180.0) * 3.2;

// Mid-sharp fill highlight (broader streak).
vec3 hFill = normalize(fillLight + viewDir);
float sFill = pow(max(dot(nRot, hFill), 0.0), 48.0) * 1.4;

// Cool rim secondary highlight (broad shine).
vec3 hRim = normalize(rimLight + viewDir);
float sRim = pow(max(dot(nRot, hRim), 0.0), 20.0) * 0.8;

// STRONG Fresnel rim: edges blaze with grazing reflection (classic metal).
float fresnel = pow(1.0 - max(dot(nRot, viewDir), 0.0), 2.5) * 1.4;

vSpecular = sKey + sFill + sRim + fresnel;

// ---- Depth-of-field fog factor ----
// Closer cubes (small distance from camera) = 1.0 (full bright)
// Far cubes = 0 (fully blended into background)
float dist = length(mvPosition.xyz);
vDepthFog = 1.0 - smoothstep(6.0, 28.0, dist);

// ---- Unified soft blue-violet tint, only subtle depth variance ----
vec3 baseColor = vec3(0.72, 0.70, 0.95);

// Slight depth-based brightness so back/front rows still have layering.
float depthShade = 0.88 + rcl.z * 0.15 - rcl.x * 0.010;
depthShade = clamp(depthShade, 0.6, 1.05);

vTintColor = baseColor * depthShade;
          `
        );

      shader.fragmentShader = shader.fragmentShader
        .split("#include <common>")
        .join(
          `
varying vec3 vTintColor;
varying float vFaceShade;
varying float vSpecular;
varying float vDepthFog;
#include <common>
          `
        )
        .split("#include <map_fragment>")
        .join(
          `
#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D( map, vMapUv );
  // Metal = darker diffuse, color comes mostly from reflection.
  vec3 metalTint = mix(vec3(1.0), vTintColor, 0.55);
  sampledDiffuseColor.rgb *= metalTint * vFaceShade * 0.85;

  // Layered specular highlight tints for chromatic metallic shimmer.
  vec3 specCore   = vec3(0.95, 0.97, 1.0);   // hot white core
  vec3 specMid    = vec3(0.55, 0.70, 1.0);   // cool blue mid
  vec3 specEdge   = vec3(0.70, 0.50, 1.0);   // violet outer chroma

  sampledDiffuseColor.rgb += specCore * vSpecular;
  sampledDiffuseColor.rgb += specMid  * vSpecular * 0.35;
  sampledDiffuseColor.rgb += specEdge * vSpecular * 0.20;

  // Depth fog: blend toward dark background when far away.
  vec3 fogColor = vec3(0.03, 0.04, 0.09);
  sampledDiffuseColor.rgb = mix(fogColor, sampledDiffuseColor.rgb, vDepthFog);
  sampledDiffuseColor.a *= mix(0.0, 1.0, vDepthFog);
  diffuseColor *= sampledDiffuseColor;
#endif
          `
        );
    };

    const mesh = new InstancedMesh(
      geom,
      material,
      rowCount * columnCount * layerCount
    );
    scene.add(mesh);

    const renderer = new WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(initWidth, initHeight);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    let frameId = 0;

    const onWindowResize = () => {
      const { width, height } = getSize();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", onWindowResize, false);

    // Scroll / wheel interaction:
    // - Scroll down  -> cubes fall (time advances forward)
    // - Scroll up    -> cubes fly back up (time rewinds)
    let scrollOffset = 0;
    let scrollVelocity = 0;
    let lastTs = 0;

    // Sensitivity for scroll-driven falling speed (smaller = slower fall)
    const SCROLL_SENSITIVITY = 0.001;
    const TOUCH_SENSITIVITY = 0.0016;

    const onWheel = (e: WheelEvent) => {
      const normalized = Math.max(-100, Math.min(100, e.deltaY));
      scrollVelocity += normalized * SCROLL_SENSITIVITY;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        lastTouchY = e.touches[0].clientY;
      }
    };
    let lastTouchY = 0;
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      const y = e.touches[0].clientY;
      const delta = lastTouchY - y;
      lastTouchY = y;
      scrollVelocity += delta * TOUCH_SENSITIVITY;
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });

    const animate = (t: number) => {
      const dt = lastTs ? (t - lastTs) / 1000 : 0;
      lastTs = t;

      scrollOffset += scrollVelocity;
      const decay = Math.pow(0.0001, dt);
      scrollVelocity *= decay;

      const tt = t / 1000;
      const idleJitter =
        Math.sin(tt * 0.45) * 0.008 +
        Math.sin(tt * 1.13 + 1.7) * 0.004 +
        Math.sin(tt * 0.21 + 0.6) * 0.006;

      time.value = scrollOffset + idleJitter;
      // Independent light rotation - keeps highlights flowing across cubes.
      lightTimeUniform.value = tt * 0.6;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      renderer.dispose();
      geom.dispose();
      material.dispose();
      map.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 w-full h-full ${className}`}
      aria-hidden="true"
    />
  );
}